import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Member } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Form } from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { session } from '@/shared/session/session';

import { fromMember, toMemberUpdate } from '../api/mappers';
import { useUpdateMember } from '../hooks/use-member-mutations';
import { useRoles } from '../hooks/use-roles';
import {
  editMemberSchema,
  type EditMemberInput,
  type EditMemberOutput,
} from '../schemas/member-form-schema';
import { assignableRoles, memberChangeErrorKey, memberRestrictions } from '../schemas/member-rules';

import { MemberAccessFields } from './member-access-fields';
import { MemberPermissionOverrides } from './member-permission-overrides';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'roleKey',
  'storeIds',
  'grantedPermissions',
  'revokedPermissions',
  'storeRoles',
]);

export interface EditMemberDialogProps {
  /** The row being edited; null closes the dialog. */
  member: Member | null;
  members: readonly Member[];
  onOpenChange: (open: boolean) => void;
}

export function EditMemberDialog({ member, members, onOpenChange }: EditMemberDialogProps) {
  const { t } = useTranslation('team');
  return (
    <Dialog open={member !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('edit.title')}</DialogTitle>
          <DialogDescription>
            {member ? `${member.user.name} · ${member.user.email}` : t('edit.description')}
          </DialogDescription>
        </DialogHeader>
        {member ? (
          // Keyed so a different row starts from its own values instead of the previous form state.
          <EditMemberForm
            key={member.id}
            member={member}
            members={members}
            onDone={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function EditMemberForm({
  member,
  members,
  onDone,
}: {
  member: Member;
  members: readonly Member[];
  onDone: () => void;
}) {
  const { t } = useTranslation(['team', 'common']);
  const { me } = session.useSession();
  const roles = useRoles();
  const update = useUpdateMember();
  const message = useErrorMessage();
  const actor = me ? { userId: me.user.id, roleKey: me.role.key } : null;
  const restrictions = actor ? memberRestrictions(actor, member, members) : null;
  const assignable = assignableRoles(
    roles.data ?? [],
    actor?.roleKey ?? '',
    me?.permissions,
    member.role.key,
  );
  const form = useForm<EditMemberInput, unknown, EditMemberOutput>({
    resolver: useI18nResolver(editMemberSchema),
    defaultValues: fromMember(member),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync({ id: member.id, body: toMemberUpdate(values) });
      onDone();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const key = memberChangeErrorKey(error);
      form.setError('root.server', { type: 'server', message: key ? t(key) : message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <p className="text-muted-foreground text-sm">{t('team:edit.description')}</p>
        <MemberAccessFields
          roles={assignable}
          stores={me?.stores ?? []}
          roleLocked={restrictions?.roleLocked ?? false}
        />
        <MemberPermissionOverrides
          roles={roles.data ?? []}
          assignable={assignable}
          stores={me?.stores ?? []}
          held={me?.permissions ?? []}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? t('common:actions.saving') : t('team:edit.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
